import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getContracts } from 'modules/contracts/actions';
import useLiterals from 'utils/hooks/useLiterals';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import StepsProgress from 'components/UI/StepsProgress';
import Box from 'components/UI/Box/Box';
import Select from 'components/UI/Select';
import Button from 'components/UI/Button';
import { ReactComponent as CardIcon } from 'assets/icons/card.svg';
import { ReactComponent as BillingIcon } from 'assets/icons/money_red.svg';
import { formatCurrency } from 'utils/formatCurrency';
import PaymentReference from 'components/PaymentReference';
import BBVA from 'components/BBVA';
import MercadoPago from 'components/MercadoPago';
import useOutOfOffice from 'utils/hooks/useOutOfOffice';
import PaymentOutOfHour from 'components/PaymentOutOfHour';
import { renderIconCategories } from 'components/PaymentBox';
import Translate from 'components/Translate';
import InputAmountSteps from 'components/UI/InputAmountSteps';
import { PAYCASH_MAX_AMOUNT, PROVIDER_TYPE, CONTRACT_STATUS } from 'constants/global';
import { formatContractPaymentOptions } from 'utils/formatContratPaymentOption';
import ContractPaymentOptions from 'routes/ContractPayment/components/ContractPaymentOptions';
import { useMercadoPagoVisible } from 'modules/mercadopago/useMercadoPagoVisible';
import { formatDate } from 'utils/formatDate';
import { mergePaymentOrdersWithEmpaques } from 'utils/paymentSummaryEmpaque';
import PaymentResumeEmpaqueRow from 'routes/ContractPayment/components/PaymentResumeEmpaqueRow';
import { ROUTE_PATH } from 'routes';
import './styles.scss';

const MultiplePayment = () => {
  const literals = useLiterals('contractPay');
  const allContracts = useSelector((state) => state.contracts);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { schedule: paymentsSchedule, isOutOfHour } = useOutOfOffice();
  const { visible: showMercadoPago } = useMercadoPagoVisible();
  const [form, setForm] = useState({});
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [step, setStep] = useState(0);
  const [showOptions, setShowOptions] = useState([]);
  const [multiplePaymentSummary, setMultiplePaymentSummary] = useState(null);
  const cardsContainerRef = useRef(null);

  const contracts = useMemo(() => {
    const data = [];
    if (allContracts?.list) {
      allContracts.list.forEach((contract) => {
        if (contract.OpcionesPago.length > 0) {
          data.push({
            ...contract,
            options: formatContractPaymentOptions(contract?.OpcionesPago, literals),
          });
        }
      });
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allContracts]);

  const { totalAmount, validAmount } = useMemo(() => (
    {
      totalAmount: parseFloat(Object.values(form).reduce((acc, curr) => acc + parseFloat(curr?.amount || 0), 0.00).toFixed(2)),
      validAmount: Object.values(form).every((c) => !c.error),
    }
  ), [form]);
  const paycashAvailable = totalAmount <= PAYCASH_MAX_AMOUNT;

  useEffect(() => {
    if (!contracts.loaded) {
      dispatch(getContracts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickStep0 = () => {
    if (!paycashAvailable && paymentMethod === PROVIDER_TYPE.PAYCASH) {
      setPaymentMethod(null);
    }
    setStep(1);
  };

  const handleSelectPayment = (option, contractId) => {
    const newValue = { ...form };
    if (!option) {
      delete newValue[contractId];
    } else {
      newValue[contractId] = {
        ...option,
        amount: option.amount,
        originalAmount: option.amount,
        error: false,
      };
    }
    setForm(newValue);
  };

  const handleChangeAmount = (value, contractId) => {
    const newValue = { ...form };
    const oldValue = newValue[contractId];

    const error = (oldValue?.min && value < oldValue.min && value !== oldValue?.amount) || (oldValue?.max && value > oldValue.max);

    newValue[contractId] = {
      ...newValue[contractId],
      amount: value,
      error,
    };
    setForm(newValue);
  };

  const renderStep0 = () => {
    return (
      <>
        <div className='contracts-list'>
          {contracts.map((contract) => {
            const selected = form[contract.Numero] || null;
            const articleDescription = contract?.Descripcion?.trim?.() || '';
            return (
              <Box className='contract-item mb-6' key={contract.Numero}>
                <div className='mb-4'>
                  <div className='flex gap-4 items-center mb-2'>
                    {renderIconCategories(contract.Ramo, contract.isAuto)}
                    <div className='contract-item-header-text'>
                      <h3 className='mb-0'>{contract.Numero}</h3>
                      {articleDescription ? (
                        <p className='contract-item-description mb-0'>
                          {articleDescription}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <p>
                    <b>{`${literals.office}: `}</b>
                    {contract.NombreSucursal}
                  </p>
                  <p className='mb-0'>
                    <b>{`${literals.pendingCapital}: `}</b>
                    {formatCurrency(contract.CapitalRestante)}
                  </p>
                </div>
                {contract.seRealizoPago ? (
                  <Select
                    value='0'
                    options={[{
                      label: literals.onlyOnePayment,
                      value: '0',
                    }]}
                    onChange={() => {}}
                    disabled
                  />
                ) : (
                  <>
                    <Select
                      value={showOptions.includes(contract.Numero) ? '1' : '0'}
                      options={[
                        {
                          label: literals.noPaymentOption,
                          value: '0',
                        },
                        {
                          label: literals.showPaymentOptions,
                          value: '1',
                        },
                      ]}
                      onChange={(v) => {
                        if (v === '1') {
                          setShowOptions([...showOptions, contract.Numero]);
                        } else {
                          setShowOptions(showOptions.filter((c) => c !== contract.Numero));
                          handleSelectPayment(null, contract.Numero);
                        }
                      }}
                      canBeEmpty={false}
                    />
                    {showOptions.includes(contract.Numero) && (
                      <ContractPaymentOptions
                        className='mt-4'
                        label={false}
                        literals={literals}
                        options={contract.options}
                        selectedPayment={selected?.key || '0'}
                        onSelectPayment={(v) => handleSelectPayment(contract.options.find((c) => c.key === v), contract.Numero)}
                      />
                    )}
                  </>
                )}
                {selected && (
                  <>
                    <div className='mt-2 mb-1 font-bold'>{literals.amount}</div>
                    <InputAmountSteps
                      value={selected.amount}
                      originalValue={selected.amount}
                      min={selected.min}
                      max={selected.max}
                      error={selected.error}
                      onChange={(v) => handleChangeAmount(v, contract.Numero)}
                    />
                  </>
                )}
              </Box>
            );
          })}
        </div>
        <Button
          onClick={handleClickStep0}
          text={`${literals.common.next} ${totalAmount ? `(${formatCurrency(totalAmount)})` : ''}`}
          className='mt-4'
          disabled={totalAmount === 0 || !validAmount}
        />
      </>
    );
  };

  const renderStep1 = () => {
    return (
      <>
        <h3 className='mb-4'>{literals.multiplePayment}</h3>
        <Translate
          literal={literals.multipleResume}
          vars={{ contracts: Object.keys(form).length, amount: formatCurrency(totalAmount) }}
        />
        <h3 className='mb-4'>{literals.paymentMethod}</h3>
        <div className='grid-container' ref={cardsContainerRef}>
          <Box
            className={`${paymentMethod === PROVIDER_TYPE.BANCOMER && 'selected'} payment-option-box flex items-start text-center`}
            onClick={() => setPaymentMethod(PROVIDER_TYPE.BANCOMER)}
          >
            <div className='py-2'>
              <CardIcon className='' width={35} />
              <p className='mt-4 mb-0 text-xs'>{literals.cardBancomerDescription}</p>
              <p className='mt-2 mb-0 text-sm font-semibold'>{literals.card}</p>
            </div>
          </Box>
          {showMercadoPago && (
            <Box
              className={`${paymentMethod === PROVIDER_TYPE.MERCADO_PAGO && 'selected'} payment-option-box flex items-start text-center`}
              onClick={() => setPaymentMethod(PROVIDER_TYPE.MERCADO_PAGO)}
            >
              <div className='py-2'>
                <CardIcon className='' width={35} />
                <p className='mt-4 mb-0 text-xs'>{literals.mercadopagoDescription}</p>
                <p className='mt-2 mb-0 text-sm font-semibold'>{literals.mercadopago}</p>
              </div>
            </Box>
          )}
          <Box
            className={`${paymentMethod === PROVIDER_TYPE.PAYCASH && 'selected'} payment-option-box flex items-start text-center ${paycashAvailable ? '' : 'disabled'}`}
            onClick={() => paycashAvailable && setPaymentMethod(PROVIDER_TYPE.PAYCASH)}
          >
            <div className='py-2'>
              <BillingIcon className='' width={35} />
              <p className='mt-4 mb-0 text-xs'>{literals.referencePayment}</p>
              <p className='mt-2 mb-0 text-sm font-semibold'>{literals.paycash}</p>
              {!paycashAvailable && (
                <Translate
                  className='mb-0 text-xs'
                  literal={literals.maxAmountPaycash}
                  vars={{ amount: formatCurrency(PAYCASH_MAX_AMOUNT) }}
                />
              )}
            </div>
          </Box>
          <Box
            className={`${paymentMethod === PROVIDER_TYPE.PUNTORED && 'selected'} payment-option-box flex items-start text-center`}
            onClick={() => setPaymentMethod(PROVIDER_TYPE.PUNTORED)}
          >
            <div className='py-2'>
              <BillingIcon className='' width={35} />
              <p className='mt-4 mb-0 text-xs'>{literals.referencePayment}</p>
              <p className='mt-2 mb-0 text-sm font-semibold'>{literals.puntored}</p>
            </div>
          </Box>
        </div>
        <Button
          onClick={() => setStep(2)}
          className='mt-6'
          type='primary'
          disabled={!paymentMethod}
          text={literals.pay}
        />
      </>
    );
  };

  const renderStep2 = () => {
    const orders = Object.keys(form).map((cid) => ({
      contract_id: cid,
      payment_key: form[cid].paymentKey,
      contract_amount: form[cid].originalAmount,
      amount: parseFloat(form[cid].amount),
      payment_name: form[cid].title,
    }));

    if (paymentMethod === PROVIDER_TYPE.PAYCASH || paymentMethod === PROVIDER_TYPE.PUNTORED) {
      return (
        <PaymentReference
          provider={paymentMethod}
          amount={totalAmount}
          orders={orders}
          isMultiple
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.BANCOMER) {
      return (
        <BBVA
          orders={orders}
          amount={totalAmount}
          isMultiple
          onClose={() => {
            setStep(1);
          }}
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.MERCADO_PAGO) {
      return (
        <MercadoPago
          amount={totalAmount}
          orders={orders}
          isMultiple
          onClose={() => {
            setPaymentMethod(null);
            navigate(ROUTE_PATH.PAYMENTS_MULTIPLE, { replace: true });
          }}
          onSuccess={(response) => {
            setMultiplePaymentSummary({
              orders: mergePaymentOrdersWithEmpaques(orders, response),
              totalAmount,
              date: new Date(),
              providerType: PROVIDER_TYPE.MERCADO_PAGO,
              status: CONTRACT_STATUS.COMPLETED,
            });
          }}
          onPending={(pendingResult) => {
            setMultiplePaymentSummary({
              orders: mergePaymentOrdersWithEmpaques(orders, pendingResult),
              totalAmount,
              date: new Date(),
              providerType: PROVIDER_TYPE.MERCADO_PAGO,
              status: CONTRACT_STATUS.PENDING,
              warningMessage: pendingResult?.message,
              mpStatus: pendingResult?.status,
              mpStatusDetail: pendingResult?.statusDetail,
              mpPaymentId: pendingResult?.paymentId,
            });
          }}
        />
      );
    }
    return <h1>Próximamente</h1>;
  };

  const handleBack = () => {
    if (multiplePaymentSummary) {
      setMultiplePaymentSummary(null);
      setStep(0);
      setPaymentMethod(null);
      navigate(ROUTE_PATH.PAYMENTS);
      return;
    }
    if (step > 0) {
      setStep(step - 1);
      return;
    }
    navigate(-1);
  };

  const renderMultiplePaymentSummary = () => {
    const summary = multiplePaymentSummary;
    if (!summary) return null;
    return (
      <div className='payment-resume'>
        <h1 className='mb-6 text-primary'>{literals.paymetResume}</h1>
        {summary.warningMessage && (
          <div className='alert alert-warning mb-4' role='alert'>
            <strong>{literals.warningTitle || 'Atención'}</strong>
            {' '}
            {summary.warningMessage}
          </div>
        )}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.contracts}</h2>
          <h3 className='text-neutral-500'>{summary.orders?.length || 0}</h3>
        </div>
        {(summary.orders || []).map((order) => (
          <div key={order.contract_id} className='payment-resume-contract-info mb-2'>
            <div className='d-flex justify-content-between'>
              <h2 className='font-bold'>{order.contract_id}</h2>
              <h3 className='text-gray'>{formatCurrency(order.amount)}</h3>
            </div>
            <PaymentResumeEmpaqueRow
              payment={order}
              label={literals.empaque}
            />
          </div>
        ))}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.date}</h2>
          <h3 className='text-neutral-500'>{formatDate(summary.date, 'DD/MM/YYYY')}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.paymentMethod2}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.mercadopago}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.status}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.statusValues[summary.status] || summary.status}</h3>
        </div>
        <div className='separator my-4' />
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.totalAmountPaid}</h2>
          <h3 className='text-primary font-bold'>{formatCurrency(summary.totalAmount)}</h3>
        </div>
        <Button
          className='mt-6'
          type='primary'
          text={literals.common.back}
          onClick={() => {
            setMultiplePaymentSummary(null);
            setStep(0);
            setPaymentMethod(null);
            navigate(ROUTE_PATH.PAYMENTS);
          }}
        />
      </div>
    );
  };

  return (
    <Layout
      title={literals.title}
      bottomMenu
      onBack={handleBack}
    >
      <AppShell>
        <div className='contract-payment__container'>
          <Box padding='sm' className='mb-4'>
            {isOutOfHour && <PaymentOutOfHour schedule={paymentsSchedule} />}
            {!isOutOfHour && multiplePaymentSummary && renderMultiplePaymentSummary()}
            {!isOutOfHour && !multiplePaymentSummary && (
              <>
                <StepsProgress
                  className='mb-4 w-full'
                  steps={[
                    { label: literals.contracts, value: 0 },
                    { label: literals.paymentMethod2, value: 1 },
                    { label: literals.pay, value: 2 },
                  ]}
                  current={step}
                />
                {step === 0 && renderStep0()}
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
              </>
            )}
          </Box>
        </div>
      </AppShell>
    </Layout>
  );
};

export default MultiplePayment;
